import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { proofReelHighlights, mainProofReelVideo } from '../mockData';
import { useState } from 'react';
import { X } from 'lucide-react';

const ProofReelSection = React.forwardRef((props, ref) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };
  
  const closeVideoModal = () => {
    setSelectedVideo(null);
  };
  
  return (
    <section ref={ref} className="py-20 px-4 bg-[#061018] relative overflow-hidden">
      {/* Abstract background elements */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="abstract-blob animate-float" style={{ 
          top: '15%', 
          right: '10%', 
          width: '500px', 
          height: '500px', 
          background: 'radial-gradient(circle, rgba(255, 101, 0, 0.25), transparent 70%)',
          filter: 'blur(60px)'
        }}></div>
        <div className="abstract-blob animate-float-slow" style={{ 
          bottom: '20%', 
          left: '5%', 
          width: '400px', 
          height: '400px', 
          background: 'radial-gradient(circle, rgba(255, 140, 50, 0.2), transparent 70%)',
          filter: 'blur(70px)'
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-12 text-center">
          What Augmented Cinematic Proof <span className="text-[#FF6500]">Looks Like</span>
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {proofReelHighlights.map((item, index) => (
            <div 
              key={index}
              className="bg-[#0a1525] rounded-lg border border-[#152840] hover:border-[#FF6500]/30 transition-all duration-300 overflow-hidden"
            >
              {/* Video Embed - Clickable with overlay */}
              {item.videoUrl && (
                <div className="aspect-video w-full bg-black relative">
                  <iframe
                    src={item.videoUrl}
                    title={item.title}
                    className="w-full h-full pointer-events-none"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  {/* Invisible clickable overlay */}
                  <div 
                    className="absolute inset-0 cursor-pointer z-10"
                    onClick={() => openVideoModal(item)}
                  />
                </div>
              )}
              
              {/* PDF Preview - Character Design Sheet Image */}
              {item.pdfUrl && (
                <a 
                  href={item.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-video w-full bg-gradient-to-br from-[#152840] to-[#0a1525] relative overflow-hidden block group cursor-pointer"
                >
                  {/* Character Design Sheet Image */}
                  <img 
                    src="https://customer-assets.emergentagent.com/job_f8a903f6-d7ce-46eb-8c10-cd5907525f5b/artifacts/zk9b50k4_image.png"
                    alt="Character Design Sheet - Design Bible Sample"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#FF6500]/90 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <p className="text-white text-lg font-semibold">View Full Design Bible</p>
                      <p className="text-white/70 text-sm">Click to open PDF</p>
                    </div>
                  </div>
                </a>
              )}
              
              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-white mb-6">{item.title}</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-[#FF6500] uppercase tracking-wider mb-2">What it is</h4>
                    <p className="text-base text-[#aaa] leading-relaxed">{item.whatItIs}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-[#FF6500] uppercase tracking-wider mb-2">How it's used</h4>
                    <p className="text-base text-[#aaa] leading-relaxed">{item.howItsUsed}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-[#FF6500] uppercase tracking-wider mb-2">Why it matters</h4>
                    <p className="text-base text-[#aaa] leading-relaxed">{item.whyItMatters}</p>
                  </div>
                </div>
                
                {/* View/Download button for PDF */}
                {item.pdfUrl && (
                  <a 
                    href={item.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#FF6500] hover:bg-[#E65A00] text-black font-medium rounded-full transition-all duration-300 text-sm uppercase tracking-wider hover:scale-105"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Full Design Bible
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Video Modal Popup */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 animate-fadeIn"
          onClick={closeVideoModal}
        >
          <div 
            className="relative w-full max-w-6xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeVideoModal}
              className="absolute -top-12 right-0 p-2 text-white hover:text-[#FF6500] transition-colors duration-300"
              aria-label="Close video"
            >
              <X className="w-8 h-8" />
            </button>
            
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden shadow-2xl shadow-[#FF6500]/20">
              <iframe
                src={selectedVideo.videoUrl}
                title={selectedVideo.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            <div className="mt-6 text-center">
              <h3 className="text-2xl font-semibold text-white mb-2">{selectedVideo.title}</h3>
              <p className="text-[#aaa]">{selectedVideo.whatItIs}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
});

export default ProofReelSection;